import React, {useCallback, useEffect, useMemo, useState} from "react";
import Layout from "../layout/Layout";

import api, {apiV2, platformAPI, platformFormAPI} from "../../../api/axiosConfig";
import LogoUpload from "../components/LogoUpload";
import BasicText from "../../../components/form-v2/basic-text";
import {BlueButton} from "../../../components/ui/Buttons";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {isAxiosError} from "axios";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import {AlternativeIDs, Client} from "../../../types/userManagement";
import {MultiInputMultiField} from "../../../components/form-v2/multi-input/MultiInputMultiField";

const DEFAULT_APP_COLOUR = "#2D166F";
const DEFAULT_PLATFORM_COLOURS = {
    mode: "gradient" as const,
    primary: "#2D166F",
    secondary: "#007584",
    gradientCenter: 75,
    gradientBlend: 50,
};

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

const getGradientControls = (input?: { gradientCenter?: number; gradientBlend?: number }) => {
    return {
        gradientCenter: clampPercent(input?.gradientCenter ?? DEFAULT_PLATFORM_COLOURS.gradientCenter),
        gradientBlend: clampPercent(input?.gradientBlend ?? DEFAULT_PLATFORM_COLOURS.gradientBlend),
    };
};

const smoothGradient = (primary: string, secondary: string, center: number, blend: number) => {
    const gradientCenter = clampPercent(center);
    const gradientBlend = clampPercent(blend);
    const start = clampPercent(gradientCenter - gradientBlend / 2);
    const end = clampPercent(gradientCenter + gradientBlend / 2);
    return `linear-gradient(90deg, ${primary} 0%, ${primary} ${start}%, ${secondary} ${end}%, ${secondary} 100%)`;
};

const ClientEdit: React.FC = () => {


    const {id} = useParams();


    const [loading, setLoading] = useState(true);

    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("")
    const [alternativeIDs, setAlternativeIDs] = useState<AlternativeIDs>([]);
    const [blockVoyageManagerInvites, setBlockVoyageManagerInvites] = useState(false);
    const [appColour, setAppColour] = useState<string>(DEFAULT_APP_COLOUR);
    const [platformColours, setPlatformColours] = useState<{
        mode: "single" | "gradient";
        colour?: string;
        primary?: string;
        secondary?: string;
        gradientCenter?: number;
        gradientBlend?: number;
    }>(DEFAULT_PLATFORM_COLOURS);

    const navigate = useNavigate();

    const loadClient = async() => {
        try {
            const res = await apiV2.get<Client>(`clients/${id}`);
    
            setName(res.data?.name);
            setAlternativeIDs(res.data.alternativeIDs ?? []);
            setBlockVoyageManagerInvites(res.data.options?.blockVoyageManagerInvites ?? false);
            setAppColour(res.data.appColour?.trim() || DEFAULT_APP_COLOUR);
            setPlatformColours(res.data.platformColours ?? DEFAULT_PLATFORM_COLOURS);
    
        } catch (e){
            setError("error loading client");
        }
        setLoading(false);
    };

    useEffect(() => {
        if(!id){
            return
        }

        loadClient()
    }, [id]);


    const save = useCallback(async () => {
        setSaving(true);
    
        try {
            await apiV2.patch(`clients/${id}`, {
                name,
                alternativeIDs,
                options: { blockVoyageManagerInvites },
                appColour,
                platformColours,
            });
        } catch (e) {
            if (isAxiosError(e) && e?.response?.data?.error) {
                setError(e.response.data.error);
            } else {
                setError("An error has occurred");
            }
            setSaving(false);
            return;
        }
    
        setSaving(false);
    
        const params = new URLSearchParams();
        params.set("message", "client has been updated");
        navigate(`/management/clients?${params.toString()}`);
        return;
    }, [name, alternativeIDs, blockVoyageManagerInvites, appColour, platformColours]);
    

    return <Layout
        active={"clients"}
    >

        <div className="flex flex-row gap-8">
            {loading && <div className="bg-white shadow-md p-4 w-1/2 flex flex-col gap-8 items-center">
                <LoadingSpinner/>
            </div>}
            <div className="flex flex-col gap-8 w-1/3">
                    {!loading && id && (
                        <LogoUpload
                            label="Platform Logo"
                            fetchLogo={async () => {
                                const base = (process.env.REACT_APP_PLATFORM_API ?? "").replace(/\/?$/, "/");
                                const res = await fetch(`${base}assets/logo/${id}?noDefault`);
                                if (!res.ok) return null;
                                return res.blob();
                            }}
                            onUpload={async (file) => {
                                const fd = new FormData();
                                fd.append("file", file);
                                await platformFormAPI.post(`api/assets/logo/${id}`, fd);
                            }}
                            onDelete={async () => {
                                await platformAPI.delete(`api/assets/logo/${id}`);
                            }}
                        />
                    )}
                    {!loading && id && (
                        <LogoUpload
                            label="App Logo"
                            fetchLogo={async () => {
                                const base = (process.env.REACT_APP_PLATFORM_API ?? "").replace(/\/?$/, "/");
                                const res = await fetch(`${base}assets/app-logo/${id}?noDefault`);
                                if (!res.ok) return null;
                                return res.blob();
                            }}
                            onUpload={async (file) => {
                                const fd = new FormData();
                                fd.append("file", file);
                                await platformFormAPI.post(`api/assets/app-logo/${id}`, fd);
                            }}
                            onDelete={async () => {
                                await platformAPI.delete(`api/assets/app-logo/${id}`);
                            }}
                        />
                    )}
                    {!loading && id && (
                        <ClientColourForm
                            id={id}
                            type="platform"
                            initialPlatformColours={platformColours}
                        />
                    )}

                    {!loading && id && (
                        <ClientColourForm
                            id={id}
                            type="app"
                            initialAppColour={appColour}
                        />
                    )}
                </div>


            {!loading && <div className="bg-white shadow-md p-4 w-2/3 flex flex-col gap-8">
                <p className="text-md text-left pb-2 font-semibold">Edit Client</p>
                <BasicText value={name} onChange={setName} label="Name"/>

                    <MultiInputMultiField label={"External IDs"}
                                          initialItems={alternativeIDs}
                                          columns={3}
                                          unboldLabel
                                          onChange={setAlternativeIDs}
                                          placeholders={{
                                              type: "Type",
                                              value: "Value",
                                          }}
                                          required={[
                                              "type",
                                              "value"
                                          ]}
                    />


                <div className="flex flex-col gap-3">
                    <p className="text-sm font-semibold">Options</p>
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={blockVoyageManagerInvites}
                            onChange={(e) => setBlockVoyageManagerInvites(e.target.checked)}
                            className="w-4 h-4 cursor-pointer"
                        />
                        <span className="text-sm">Block Voyage Manager Invites</span>
                    </label>
                </div>

                <BlueButton text={"Update Client"} disabled={name == ""} onClick={save} loading={saving}/>
                {error && <p className={"text-center text-red"}>{error}</p>}
            </div>}
        </div>

    </Layout>

}




type ClientColourFormProps = {
    id: string;
    type: "platform" | "app";
    initialPlatformColours?: {
        mode: "single" | "gradient";
        colour?: string;
        primary?: string;
        secondary?: string;
        gradientCenter?: number;
        gradientBlend?: number;
    };
    initialAppColour?: string;
};

    const ClientColourForm: React.FC<ClientColourFormProps> = ({
        id,
        type,
        initialPlatformColours,
        initialAppColour,
    }) => {
    const [mode, setMode] = useState<"single" | "gradient">(DEFAULT_PLATFORM_COLOURS.mode);
    const [primary, setPrimary] = useState(DEFAULT_PLATFORM_COLOURS.primary);
    const [secondary, setSecondary] = useState(DEFAULT_PLATFORM_COLOURS.secondary);
    const [gradientCenter, setGradientCenter] = useState(DEFAULT_PLATFORM_COLOURS.gradientCenter);
    const [gradientBlend, setGradientBlend] = useState(DEFAULT_PLATFORM_COLOURS.gradientBlend);
    const [preview, setPreview] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [confirmAction, setConfirmAction] = useState<null | "update" | "reset">(null);

    const isValidHex = (val: string) => /^#([0-9A-F]{3}){1,2}$/i.test(val);

        useEffect(() => {
        if (type === "platform" && initialPlatformColours) {
            setMode(initialPlatformColours.mode);

            if (initialPlatformColours.mode === "gradient") {
                setPrimary(initialPlatformColours.primary ?? DEFAULT_PLATFORM_COLOURS.primary);
                setSecondary(initialPlatformColours.secondary ?? DEFAULT_PLATFORM_COLOURS.secondary);
                const controls = getGradientControls(initialPlatformColours);
                setGradientCenter(controls.gradientCenter);
                setGradientBlend(controls.gradientBlend);
            } else {
                setPrimary(initialPlatformColours.colour ?? DEFAULT_PLATFORM_COLOURS.primary);
                setGradientCenter(DEFAULT_PLATFORM_COLOURS.gradientCenter);
                setGradientBlend(DEFAULT_PLATFORM_COLOURS.gradientBlend);
            }
        }

        if (type === "app") {
            setPrimary(initialAppColour?.trim() || DEFAULT_APP_COLOUR);
        }
    }, [initialPlatformColours, initialAppColour, type]);

    // preview updater
    useEffect(() => {
        if (type === "platform") {
            if (mode === "gradient" && isValidHex(primary) && isValidHex(secondary)) {
                setPreview(smoothGradient(primary, secondary, gradientCenter, gradientBlend));
            } else if (isValidHex(primary)) {
                setPreview(primary);
            }
        } else {
            if (isValidHex(primary)) setPreview(primary);
        }
    }, [primary, secondary, gradientCenter, gradientBlend, mode, type]);

	    const title = type === "platform" ? "Set Platform Colours" : "Set App Colours";
	    const initialPlatformState = useMemo(() => {
	        const source = initialPlatformColours ?? DEFAULT_PLATFORM_COLOURS;
		        if (source.mode === "gradient") {
                    const controls = getGradientControls(source);
		            return {
		                mode: "gradient" as const,
		                primary: source.primary ?? DEFAULT_PLATFORM_COLOURS.primary,
		                secondary: source.secondary ?? DEFAULT_PLATFORM_COLOURS.secondary,
                        gradientCenter: controls.gradientCenter,
                        gradientBlend: controls.gradientBlend,
		            };
		        }
	        return {
	            mode: "single" as const,
	            primary: source.colour ?? DEFAULT_PLATFORM_COLOURS.primary,
	        };
	    }, [initialPlatformColours]);
	    const initialAppState = useMemo(
	        () => (initialAppColour?.trim() || DEFAULT_APP_COLOUR),
	        [initialAppColour],
	    );
    const [savedPlatformState, setSavedPlatformState] = useState(initialPlatformState);
    const [savedAppState, setSavedAppState] = useState(initialAppState);

    useEffect(() => {
        setSavedPlatformState(initialPlatformState);
    }, [initialPlatformState]);

    useEffect(() => {
        setSavedAppState(initialAppState);
    }, [initialAppState]);

	    const hasChanges = useMemo(() => {
	        if (type === "platform") {
	            if (mode !== savedPlatformState.mode) {
	                return true;
	            }
	            if (primary !== savedPlatformState.primary) {
	                return true;
	            }
		            if (mode === "gradient") {
                    if (gradientCenter !== ("gradientCenter" in savedPlatformState ? savedPlatformState.gradientCenter : DEFAULT_PLATFORM_COLOURS.gradientCenter)) {
                        return true;
                    }
                    if (gradientBlend !== ("gradientBlend" in savedPlatformState ? savedPlatformState.gradientBlend : DEFAULT_PLATFORM_COLOURS.gradientBlend)) {
                        return true;
                    }
		                return secondary !== ("secondary" in savedPlatformState ? savedPlatformState.secondary : DEFAULT_PLATFORM_COLOURS.secondary);
		            }
		            return false;
		        }
		        return primary !== savedAppState;
	    }, [type, mode, primary, secondary, gradientCenter, gradientBlend, savedPlatformState, savedAppState]);
	    const canPreview = useMemo(() => {
	        if (type !== "platform") return false;
	        if (mode === "gradient") {
	            return isValidHex(primary) && isValidHex(secondary);
	        }
	        return isValidHex(primary);
	    }, [type, mode, primary, secondary]);
    const canSave = useMemo(() => {
        if (!hasChanges) return false;
        if (type === "platform") {
            return mode === "gradient"
                ? isValidHex(primary) && isValidHex(secondary)
                : isValidHex(primary);
        }
        return isValidHex(primary);
    }, [hasChanges, type, mode, primary, secondary]);

    const platformStyle = (m: "single" | "gradient", p: string, s: string, center: number, blend: number) =>
        m === "gradient" ? smoothGradient(p, s, center, blend) : p;

    const beforePreview = useMemo(() => {
        if (type === "platform") {
            return platformStyle(
                savedPlatformState.mode,
                savedPlatformState.primary,
                savedPlatformState.mode === "gradient"
                    ? (savedPlatformState.secondary ?? DEFAULT_PLATFORM_COLOURS.secondary)
                    : DEFAULT_PLATFORM_COLOURS.secondary,
                savedPlatformState.mode === "gradient"
                    ? (savedPlatformState.gradientCenter ?? DEFAULT_PLATFORM_COLOURS.gradientCenter)
                    : DEFAULT_PLATFORM_COLOURS.gradientCenter,
                savedPlatformState.mode === "gradient"
                    ? (savedPlatformState.gradientBlend ?? DEFAULT_PLATFORM_COLOURS.gradientBlend)
                    : DEFAULT_PLATFORM_COLOURS.gradientBlend,
            );
        }
        return savedAppState;
    }, [type, savedPlatformState, savedAppState]);

    const afterPreview = useMemo(() => {
        if (confirmAction === "reset") {
            if (type === "platform") {
                return platformStyle(
                    DEFAULT_PLATFORM_COLOURS.mode,
                    DEFAULT_PLATFORM_COLOURS.primary,
                    DEFAULT_PLATFORM_COLOURS.secondary,
                    DEFAULT_PLATFORM_COLOURS.gradientCenter,
                    DEFAULT_PLATFORM_COLOURS.gradientBlend,
                );
            }
            return DEFAULT_APP_COLOUR;
        }
        if (type === "platform") {
            return platformStyle(mode, primary, secondary, gradientCenter, gradientBlend);
        }
        return primary;
    }, [confirmAction, type, mode, primary, secondary, gradientCenter, gradientBlend]);

    const beforeLabel = useMemo(() => {
        if (type === "platform") {
            if (savedPlatformState.mode === "gradient") {
                return `${savedPlatformState.primary} -> ${"secondary" in savedPlatformState ? savedPlatformState.secondary : DEFAULT_PLATFORM_COLOURS.secondary} (Center ${savedPlatformState.gradientCenter ?? DEFAULT_PLATFORM_COLOURS.gradientCenter}% / Blend ${savedPlatformState.gradientBlend ?? DEFAULT_PLATFORM_COLOURS.gradientBlend}%)`;
            }
            return savedPlatformState.primary;
        }
        return savedAppState;
    }, [type, savedPlatformState, savedAppState]);

    const afterLabel = useMemo(() => {
        if (confirmAction === "reset") {
            return type === "platform"
                ? `${DEFAULT_PLATFORM_COLOURS.primary} -> ${DEFAULT_PLATFORM_COLOURS.secondary} (Center ${DEFAULT_PLATFORM_COLOURS.gradientCenter}% / Blend ${DEFAULT_PLATFORM_COLOURS.gradientBlend}%)`
                : DEFAULT_APP_COLOUR;
        }
        if (type === "platform") {
            return mode === "gradient" ? `${primary} -> ${secondary} (Center ${gradientCenter}% / Blend ${gradientBlend}%)` : primary;
        }
        return primary;
    }, [confirmAction, type, mode, primary, secondary, gradientCenter, gradientBlend]);
	    const applyDefaultPlatformGradient = () => {
	        setMode(DEFAULT_PLATFORM_COLOURS.mode);
	        setPrimary(DEFAULT_PLATFORM_COLOURS.primary);
	        setSecondary(DEFAULT_PLATFORM_COLOURS.secondary);
            setGradientCenter(DEFAULT_PLATFORM_COLOURS.gradientCenter);
            setGradientBlend(DEFAULT_PLATFORM_COLOURS.gradientBlend);
	    };
	    const applyDefaultAppColour = () => {
	        setPrimary(DEFAULT_APP_COLOUR);
	    };

	    const save = async () => {
        setLoading(true);
        try {
            if (type === "platform") {
                await apiV2.patch(`/clients/${id}/colours/platform`, {
                    mode,
                    ...(mode === "gradient"
                        ? {
                            primary,
                            secondary,
                            gradientCenter,
                            gradientBlend,
                        }
                        : { colour: primary }),
                });
                if (mode === "gradient") {
                    setSavedPlatformState({
                        mode: "gradient",
                        primary,
                        secondary,
                        gradientCenter,
                        gradientBlend,
                    });
                } else {
                    setSavedPlatformState({
                        mode: "single",
                        primary,
                    });
                }
                const detail = mode === "gradient"
                    ? {mode: "gradient" as const, primary, secondary, gradientCenter, gradientBlend}
                    : {mode: "single" as const, colour: primary};
                window.dispatchEvent(
                    new CustomEvent("platform-colours-preview", {detail})
                );
            } else {
                await apiV2.patch(`/clients/${id}/colours/app`, {
                    colour: primary,
                });
                setSavedAppState(primary);
            }
            console.log("colours saved");
        } catch (e) {
            console.error("Error saving colours", e);
        }
        setLoading(false);
	    };

	    const remove = async () => {
        setLoading(true);
        try {
            if (type === "platform") {
                await apiV2.delete(`/clients/${id}/colours/platform`);
                setPrimary(DEFAULT_PLATFORM_COLOURS.primary);
                setSecondary(DEFAULT_PLATFORM_COLOURS.secondary);
                setGradientCenter(DEFAULT_PLATFORM_COLOURS.gradientCenter);
                setGradientBlend(DEFAULT_PLATFORM_COLOURS.gradientBlend);
                setMode(DEFAULT_PLATFORM_COLOURS.mode);
                setSavedPlatformState({
                    mode: DEFAULT_PLATFORM_COLOURS.mode,
                    primary: DEFAULT_PLATFORM_COLOURS.primary,
                    secondary: DEFAULT_PLATFORM_COLOURS.secondary,
                    gradientCenter: DEFAULT_PLATFORM_COLOURS.gradientCenter,
                    gradientBlend: DEFAULT_PLATFORM_COLOURS.gradientBlend,
                });
                window.dispatchEvent(
                    new CustomEvent("platform-colours-preview", {
                        detail: {
                            mode: DEFAULT_PLATFORM_COLOURS.mode,
                            primary: DEFAULT_PLATFORM_COLOURS.primary,
                            secondary: DEFAULT_PLATFORM_COLOURS.secondary,
                            gradientCenter: DEFAULT_PLATFORM_COLOURS.gradientCenter,
                            gradientBlend: DEFAULT_PLATFORM_COLOURS.gradientBlend,
                        },
                    })
                );
            } else {
                await apiV2.delete(`/clients/${id}/colours/app`);
                setPrimary(DEFAULT_APP_COLOUR);
                setSavedAppState(DEFAULT_APP_COLOUR);
            }
            console.log("colours removed");
        } catch (e) {
            console.error("Error removing colours", e);
        }
		        setLoading(false);
		    };
	    const previewPlatformColours = () => {
	        if (!canPreview || type !== "platform") {
	            return;
	        }
	        const detail = mode === "gradient"
	            ? {mode: "gradient" as const, primary, secondary, gradientCenter, gradientBlend}
	            : {mode: "single" as const, colour: primary};
	        window.dispatchEvent(
	            new CustomEvent("platform-colours-preview", {detail})
	        );
	    };

	    return (
	        <div className={`bg-white shadow-md rounded-xl p-4 border border-light-light-grey flex flex-col gap-4 ${type === "platform" ? "min-h-[30rem]" : ""}`}>
	            <div className="flex items-center justify-between gap-2">
	                <p className="text-md text-left font-semibold">{title}</p>
	            </div>

	            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
	                <div className="p-3 flex flex-col gap-2 md:col-span-2">
	                    <p className="text-sm font-semibold">Preview</p>
	                    <div
	                        className="w-full h-28 border border-light-light-grey rounded-lg overflow-hidden"
	                        style={{background: preview || "#F8F8FA"}}
	                    />
	                    <button
	                        type="button"
	                        onClick={type === "platform" ? applyDefaultPlatformGradient : applyDefaultAppColour}
	                        className="h-9 px-3 rounded-md border border-light-light-grey bg-white hover:bg-desaturated-grey transition text-sm mt-1"
	                    >
	                        Use Default
	                    </button>
	                </div>

	                <div className="flex flex-col gap-3 md:col-span-3">
	                    {type === "platform" && (
	                        <div className="bg-ice border border-light-light-grey rounded-lg p-1 grid grid-cols-2 gap-1">
	                            <button
	                                type="button"
	                                onClick={() => setMode("single")}
	                                className={`h-9 rounded-md text-sm transition ${mode === "single" ? "bg-white shadow-sm font-semibold" : "hover:bg-white/60"}`}
	                            >
	                                Single
	                            </button>
	                            <button
	                                type="button"
	                                onClick={() => setMode("gradient")}
	                                className={`h-9 rounded-md text-sm transition ${mode === "gradient" ? "bg-white shadow-sm font-semibold" : "hover:bg-white/60"}`}
	                            >
	                                Gradient
	                            </button>
	                        </div>
	                    )}

	                    <div className="grid grid-cols-1 gap-4">
	                        <div className="flex flex-col gap-2">
	                            <p className="text-sm font-medium">{mode === "gradient" ? "Primary Colour" : "Colour"}</p>
	                            <div className="flex items-center gap-2">
	                                <input
	                                    type="color"
	                                    value={primary}
	                                    onChange={(e) => setPrimary(e.target.value)}
	                                    className="w-14 h-11 cursor-pointer rounded-md border border-light-light-grey p-1 bg-white"
	                                />
	                                <input
	                                    type="text"
	                                    value={primary}
	                                    onChange={(e) => setPrimary(e.target.value)}
	                                    className="w-full h-11 px-3 border border-light-light-grey rounded-md"
	                                />
	                            </div>
	                        </div>

	                        {type === "platform" && mode === "gradient" && (
	                            <div className="flex flex-col gap-2">
	                                <p className="text-sm font-medium">Secondary Colour</p>
	                                <div className="flex items-center gap-2">
	                                    <input
	                                        type="color"
	                                        value={secondary}
	                                        onChange={(e) => setSecondary(e.target.value)}
	                                        className="w-14 h-11 cursor-pointer rounded-md border border-light-light-grey p-1 bg-white"
	                                    />
	                                    <input
	                                        type="text"
	                                        value={secondary}
	                                        onChange={(e) => setSecondary(e.target.value)}
	                                        className="w-full h-11 px-3 border border-light-light-grey rounded-md"
	                                    />
	                                </div>
                                    <div className="pt-1">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-medium">Gradient Center</p>
                                            <p className="text-xs text-light-grey">{gradientCenter}%</p>
                                        </div>
                                        <input
                                            type="range"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={gradientCenter}
                                            onChange={(e) => setGradientCenter(Number(e.target.value))}
                                            className="w-full mt-2"
                                        />
                                    </div>
                                    <div className="pt-1">
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm font-medium">Blend Width</p>
                                            <p className="text-xs text-light-grey">{gradientBlend}%</p>
                                        </div>
                                        <input
                                            type="range"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={gradientBlend}
                                            onChange={(e) => setGradientBlend(Number(e.target.value))}
                                            className="w-full mt-2"
                                        />
                                    </div>
	                            </div>
	                        )}
	                    </div>

	                    {type === "platform" && (
	                        <button
	                            type="button"
	                            onClick={previewPlatformColours}
	                            disabled={!canPreview}
	                            className="w-full h-10 rounded-md border border-blue bg-white text-blue hover:bg-desaturated-grey transition text-sm disabled:opacity-60 disabled:cursor-not-allowed"
	                        >
	                            Preview
	                        </button>
	                    )}
	                    <div className="grid grid-cols-2 gap-3">
	                        <BlueButton
                                text="Update"
                                onClick={() => setConfirmAction("update")}
                                loading={loading}
                                disabled={loading || !canSave}
                            />
	                        <button
	                            type="button"
	                            onClick={() => setConfirmAction("reset")}
	                            disabled={loading}
	                            className="w-full h-10 rounded-md border border-light-light-grey bg-white hover:bg-desaturated-grey transition text-sm disabled:opacity-60 disabled:cursor-not-allowed"
	                        >
	                            Reset
	                        </button>
	                    </div>
	                </div>
	            </div>
                {confirmAction && (
                    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4">
                        <div className="w-full max-w-lg bg-white rounded-xl border border-light-light-grey shadow-xl p-5 flex flex-col gap-4">
                            <p className="text-lg font-semibold">
                                {confirmAction === "update" ? "Confirm update?" : "Confirm reset?"}
                            </p>
                            <p className="text-sm text-light-grey">
                                {confirmAction === "update"
                                    ? "This will save these colours."
                                    : "This will remove saved colours and return to defaults."}
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 border border-light-light-grey rounded-lg flex flex-col gap-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-light-grey">Before</p>
                                    <div className="h-16 rounded border border-light-light-grey" style={{background: beforePreview}} />
                                    <p className="text-xs">{beforeLabel}</p>
                                </div>
                                <div className="p-3 border border-light-light-grey rounded-lg flex flex-col gap-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-light-grey">After</p>
                                    <div className="h-16 rounded border border-light-light-grey" style={{background: afterPreview}} />
                                    <p className="text-xs">{afterLabel}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setConfirmAction(null)}
                                    className="w-full h-10 rounded-md border border-light-light-grey bg-white hover:bg-desaturated-grey transition text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        if (confirmAction === "update") {
                                            await save();
                                        } else {
                                            await remove();
                                        }
                                        setConfirmAction(null);
                                    }}
                                    className="w-full h-10 rounded-md bg-blue text-white hover:bg-opacity-90 transition text-sm"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}
	        </div>
	    );
	};




ClientEdit.displayName = "ClientEdit";
export default ClientEdit;
